import {Component, forwardRef, Input, OnDestroy, OnInit} from '@angular/core';
import {
    LoadModelInfo,
    ModelType,
    PredictiveModelInfo,
    ProsumerModelInfo,
    PvModelInfo,
    ThermalLoadModelInfo
} from 'smag-commons/cfg/prediction';
import {
    AbstractControl,
    ControlValueAccessor,
    FormBuilder,
    FormControl,
    FormGroup,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validators
} from '@angular/forms';
import {Subscription} from 'rxjs';
import {first} from 'rxjs/operators';
import {CapabilitiesService} from '../capabilities.service';
import {
    POD_AVERAGE_ABSORBED_POWER_DEVICE,
    POD_AVERAGE_INJECTED_POWER_DEVICE,
    POD_INSTANT_ABSORBED_POWER_DEVICE,
    POD_INSTANT_INJECTED_POWER_DEVICE
} from 'smag-commons/cfg/hierarchy';
import {MatSelectChange} from '@angular/material/select';
import {ModelAlgorithm} from 'smag-commons/dto/prediction';
import {isBlank, isDefined, isNumber, round} from '../../commons/commons';
import {limitDecimals} from '../ec-economics';
import {WindModelInfo} from 'smag-commons/cfg/prediction/models';

export interface ResetContext {
}

export interface PredictiveModelResource {
    type: PredictiveModelResourceType;
}

export interface PredictiveModelPodResource extends PredictiveModelResource {
    type: PredictiveModelResourceType.POD;
    podType: PredictiveModelPodResourceType;
    predictiveModel?: PredictiveModelInfo;
}

export enum PredictiveModelPodResourceType {
    CONSUMER,
    PRODUCER,
    PROSUMER
}

export interface PredictiveModelPlantResource extends PredictiveModelResource {
    type: PredictiveModelResourceType.PV_PLANT | PredictiveModelResourceType.WIND_PLANT;
    nominalPower: number;
    predictiveModel: PredictiveModelInfo;
}

export enum PredictiveModelResourceType {
    POD,
    PV_PLANT,
    WIND_PLANT
}

export interface ModelDescriptor {
    modelKey: string;
    algorithm: ModelAlgorithm;
    modelTypes: ModelType[];
}

interface Measure {
    name: string;
    deviceKey: string;
}

const DEFAULT_SURFACE_POWER_DENSITY = 187;
const DEFAULT_REFERENCE_EFFICIENCY = 0.19;
const DEFAULT_PACKING_FACTOR = 0.5;
const DEFAULT_SYSTEM_EFFICIENCY = 0.85;

@Component({
    selector: 'app-predictive-model',
    templateUrl: './predictive-model.component.html',
    styleUrls: ['./predictive-model.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PredictiveModelComponent),
            multi: true
        }
    ]
})
export class PredictiveModelComponent implements OnInit, OnDestroy, ControlValueAccessor {

    private changeSubscription: Subscription;

    form: FormGroup;
    ModelType = ModelType;
    modelsDescriptors: ModelDescriptor[];
    measures: Measure[];

    readonly limitDecimals = limitDecimals;

    @Input() resource: PredictiveModelResource;
    @Input() resetValueProvider: (ctx: ResetContext) => Promise<PredictiveModelInfo>;

    constructor(private readonly fb: FormBuilder,
                private readonly capabilitiesService: CapabilitiesService) {
        this.changeSubscription = null;
        this.modelsDescriptors = [];
        this.measures = [];
        this.form = this.fb.group({modelType: new FormControl(null, [Validators.required])});
    }

    ngOnInit() {
        this.capabilitiesService.predictionCaps
            .pipe(first())
            .subscribe(caps => {
                this.modelsDescriptors = Object.keys(caps.models)
                    .map(modelKey => ({
                        modelKey,
                        algorithm: caps.models[modelKey].algorithm,
                        modelTypes: caps.models[modelKey].model_types
                    }));
            });
    }

    ngOnDestroy() {
        if (this.changeSubscription) {
            this.changeSubscription.unsubscribe();
        }
    }

    registerOnChange(fn: any): void {
        if (this.changeSubscription) {
            this.changeSubscription.unsubscribe();
        }
        this.changeSubscription = this.form.valueChanges.subscribe(formData => {
            fn(form2model(formData));
        });
    }

    registerOnTouched(fn: any): void {
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    writeValue(obj: any): void {
        this.setForm(obj as PredictiveModelInfo);
    }

    get models(): string[] {
        return this.modelsDescriptors
            .filter(desc => desc.modelTypes.includes(this.form.controls.modelType.value))
            .map(desc => desc.modelKey);
    }

    get loadActivePowerMeasures(): Measure[] {
        if (!this.isConsumer) {
            return [];
        }
        return [
            {
                name: 'Average absorbed power',
                deviceKey: POD_AVERAGE_ABSORBED_POWER_DEVICE
            },
            {
                name: 'Instant absorbed power',
                deviceKey: POD_INSTANT_ABSORBED_POWER_DEVICE
            }
        ];
    }

    get loadReactivePowerMeasures(): Measure[] {
        return [];
    }

    get thLoadPowerMeasures(): Measure[] {
        return [];
    }

    get prosumerAbsPowerMeasures(): Measure[] {
        if (!this.isProsumer) {
            return [];
        }
        return [
            {
                name: 'Average absorbed power',
                deviceKey: POD_AVERAGE_ABSORBED_POWER_DEVICE
            },
            {
                name: 'Instant absorbed power',
                deviceKey: POD_INSTANT_ABSORBED_POWER_DEVICE
            }
        ];
    }

    get prosumerInjPowerMeasures(): Measure[] {
        if (!this.isProsumer) {
            return [];
        }
        return [
            {
                name: 'Average injected power',
                deviceKey: POD_AVERAGE_INJECTED_POWER_DEVICE
            },
            {
                name: 'Instant injected power',
                deviceKey: POD_INSTANT_INJECTED_POWER_DEVICE
            }
        ];
    }

    get isConsumer() {
        return this.resource.type === PredictiveModelResourceType.POD &&
            (this.resource as PredictiveModelPodResource).podType === PredictiveModelPodResourceType.CONSUMER;
    }

    get isProducer() {
        return this.resource.type === PredictiveModelResourceType.POD &&
            (this.resource as PredictiveModelPodResource).podType === PredictiveModelPodResourceType.PRODUCER;
    }

    get isProsumer() {
        return this.resource.type === PredictiveModelResourceType.POD &&
            (this.resource as PredictiveModelPodResource).podType === PredictiveModelPodResourceType.PROSUMER;
    }

    get isPvPlant() {
        return this.resource.type === PredictiveModelResourceType.PV_PLANT;
    }

    get isWindPlant() {
        return this.resource.type === PredictiveModelResourceType.WIND_PLANT;
    }

    get isPlant() {
        return !this.isPvPlant && !this.isWindPlant;
    }

    get modelTypes() {
        if (this.isConsumer) {
            return [ModelType.LOAD];
        }
        if (this.isProsumer) {
            return [ModelType.PROSUMER];
        }
        if (this.isPvPlant) {
            return [ModelType.PV];
        }
        if (this.isWindPlant) {
            return [ModelType.WIND];
        }
        return [];
    }

    onModelTypeChange(event: MatSelectChange) {
        this.setForm({type: event.value as ModelType, preferred: true});
    }

    onClickClear() {
        let newValueModelProm = Promise.resolve(null as PredictiveModelInfo);
        if (this.resetValueProvider) {
            newValueModelProm = this.resetValueProvider({});
        }
        newValueModelProm.then(newModel => {
            this.writeValue(newModel);
        });
    }

    static predictiveModelValidator(ctrl: AbstractControl): ValidationErrors | null {
        const model = ctrl.value as PredictiveModelInfo;
        if (!model) {
            // ignore "empty" model (allows non-mandatory predictive model)
            return null;
        }
        switch (model.type) {
            case ModelType.PV:
                const pvModel = model as PvModelInfo;
                if (isBlank(pvModel.power_model) ||
                    numberParamError(pvModel.params?.s_pv) ||
                    numberParamError(pvModel.params?.eta_pv) || percentageValueError(pvModel.params?.eta_pv) ||
                    numberParamError(pvModel.params?.p_f) || percentageValueError(pvModel.params?.p_f) ||
                    numberParamError(pvModel.params?.eta_pc) || percentageValueError(pvModel.params?.eta_pc)) {
                    return {err: true};
                }
                break;
            case ModelType.WIND:
                const windModel = model as WindModelInfo;
                if (isBlank(windModel.power_model)) {
                    return {err: true};
                }
                break;
            case ModelType.LOAD:
                const loadModel = model as LoadModelInfo;
                if (isBlank(loadModel.active_power_model) ||
                    isBlank(loadModel.active_power_device) ||
                    (isBlank(loadModel.reactive_power_model) && !isBlank(loadModel.reactive_power_device)) ||
                    (!isBlank(loadModel.reactive_power_model) && isBlank(loadModel.reactive_power_device))) {
                    return {err: true};
                }
                break;
            case ModelType.THERMAL_LOAD:
                const thermalLoadModel = model as ThermalLoadModelInfo;
                if (isBlank(thermalLoadModel.power_model) || isBlank(thermalLoadModel.power_device)) {
                    return {err: true};
                }
                break;
            case ModelType.PROSUMER:
                const prosumerModel = model as ProsumerModelInfo;
                if (isBlank(prosumerModel.prosumer_model) ||
                    isBlank(prosumerModel.absorbed_power_device) ||
                    isBlank(prosumerModel.injected_power_device)
                ) {
                    return {err: true};
                }
                break;
            default:
                return null;
        }
    }

    private getSolarCellArea(): number {
        return round((this.resource as PredictiveModelPlantResource).nominalPower * 1000 / DEFAULT_SURFACE_POWER_DENSITY);
    }

    private setForm(model: PredictiveModelInfo) {
        // clear form controls
        Object.keys(this.form.controls)
            .filter(ctrl => ctrl !== 'modelType')
            .forEach(ctrl => this.form.removeControl(ctrl));
        if (!model) {
            this.form.controls.modelType.setValue(null);
            return;
        }
        switch (model.type) {
            case ModelType.PV:
                const pvModel = model as PvModelInfo;
                this.form.controls.modelType.setValue(ModelType.PV);
                this.form.addControl('powerModel', new FormControl(pvModel?.power_model, []));
                this.form.addControl('solarCellArea', new FormControl(isDefined(pvModel?.params?.s_pv) ? round(pvModel.params.s_pv) : this.getSolarCellArea(), []));
                this.form.addControl('referenceEfficiency', new FormControl(pvModel?.params?.eta_pv * 100 || DEFAULT_REFERENCE_EFFICIENCY * 100, [Validators.min(1), Validators.max(100)]));
                this.form.addControl('packingFactor', new FormControl(pvModel?.params?.p_f * 100 || DEFAULT_PACKING_FACTOR * 100, [Validators.min(1), Validators.max(100)]));
                this.form.addControl('systemEfficiency', new FormControl(pvModel?.params?.eta_pc * 100 || DEFAULT_SYSTEM_EFFICIENCY * 100, [Validators.min(1), Validators.max(100)]));
                break;
            case ModelType.WIND:
                const windModel = model as WindModelInfo;
                this.form.controls.modelType.setValue(ModelType.WIND);
                this.form.addControl('powerModel', new FormControl(windModel?.power_model, []));
                break;
            case ModelType.LOAD:
                const loadModel = model as LoadModelInfo;
                this.form.controls.modelType.setValue(ModelType.LOAD);
                this.form.addControl('activePowerModel', new FormControl(loadModel?.active_power_model, []));
                this.form.addControl('activePowerMeasure', new FormControl(loadModel?.active_power_device, []));
                this.form.addControl('reactivePowerModel', new FormControl(loadModel?.reactive_power_model, []));
                this.form.addControl('reactivePowerMeasure', new FormControl(loadModel?.reactive_power_device, []));
                break;
            case ModelType.THERMAL_LOAD:
                const thermalLoadModel = model as ThermalLoadModelInfo;
                this.form.controls.modelType.setValue(ModelType.THERMAL_LOAD);
                this.form.addControl('powerModel', new FormControl(thermalLoadModel?.power_model, []));
                this.form.addControl('powerMeasure', new FormControl(thermalLoadModel?.power_device, []));
                break;
            case ModelType.PROSUMER:
                const prosumerModel = model as ProsumerModelInfo;
                this.form.controls.modelType.setValue(ModelType.PROSUMER);
                this.form.addControl('prosumerModel', new FormControl(prosumerModel?.prosumer_model, []));
                this.form.addControl('absorbedPowerMeasure', new FormControl(prosumerModel?.absorbed_power_device, []));
                this.form.addControl('injectedPowerMeasure', new FormControl(prosumerModel?.injected_power_device, []));
                break;
            default:
                this.form.controls.modelType.setValue(null);
        }
        this.form.updateValueAndValidity();
    }
}

function form2model(formData: any): PredictiveModelInfo {
    switch (formData.modelType) {
        case ModelType.PV:
            return {
                power_model: formData.powerModel,
                params: {
                    s_pv: formData.solarCellArea,
                    eta_pv: formData.referenceEfficiency / 100,
                    p_f: formData.packingFactor / 100,
                    eta_pc: formData.systemEfficiency / 100
                },
                meteo_connector_id: undefined,
                meteo_virtual_device_id: undefined,
                type: ModelType.PV,
                preferred: true
            } as PvModelInfo;
        case ModelType.WIND:
            return {
                power_model: formData.powerModel,
                power_device: undefined,
                meteo_connector_id: undefined,
                meteo_virtual_device_id: undefined,
                type: ModelType.WIND,
                preferred: true
            } as WindModelInfo;
        case ModelType.LOAD:
            return {
                active_power_model: formData.activePowerModel,
                active_power_device: formData.activePowerMeasure,
                reactive_power_model: formData.reactivePowerModel,
                reactive_power_device: formData.reactivePowerMeasure,
                type: ModelType.LOAD,
                preferred: true
            } as LoadModelInfo;
        case ModelType.THERMAL_LOAD:
            return {
                power_model: formData.powerModel,
                power_device: formData.powerMeasure,
                type: ModelType.THERMAL_LOAD,
                preferred: true
            } as ThermalLoadModelInfo;
        case ModelType.PROSUMER:
            return {
                prosumer_model: formData.prosumerModel,
                absorbed_power_device: formData.absorbedPowerMeasure,
                injected_power_device: formData.injectedPowerMeasure,
                type: ModelType.PROSUMER,
                preferred: true
            } as ProsumerModelInfo;
        default:
            return null;
    }
}

function numberParamError(value): boolean {
    return !isDefined(value) || !isNumber(value) || value <= 0 || isNaN(value);
}

function percentageValueError(value): boolean {
    return !isDefined(value) || !isNumber(value) || value < 0.01 || value > 1;
}
